import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

interface Reply {
  id: string;
  content: string;
  is_staff: boolean;
  created_at: string;
  attachment_url: string | null;
  attachment_name: string | null;
}

interface TicketData {
  request: {
    id: string;
    request_code: string;
    name: string;
    subject: string;
    message: string;
    status: string;
    created_at: string;
  };
  replies: Reply[];
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  open: { label: 'Đang xử lý', cls: 'bg-accent-500/15 text-accent-400' },
  answered: { label: 'Đã trả lời', cls: 'bg-emerald-500/15 text-emerald-400' },
  resolved: { label: 'Đã giải quyết', cls: 'bg-secondary-500/15 text-secondary-400' },
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const sanitizeFileName = (name: string) => {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot + 1) : '';
  const clean = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 60)
    .toLowerCase();
  return ext ? `${clean}.${ext.toLowerCase()}` : clean;
};

const isImageAttachment = (reply: Reply) => {
  const ref = (reply.attachment_name || reply.attachment_url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(ref);
};

export default function RequestTracker({ prefillCode, onSearchSuccess }: { prefillCode?: string; onSearchSuccess?: (code: string) => void }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchedRef = useRef<string>('');

  const search = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setLoading(true);
    setNotFound(false);
    setError('');
    setTicket(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: trimmed });
      if (rpcError) throw rpcError;
      if (!data) {
        setNotFound(true);
      } else {
        setTicket(data as TicketData);
        onSearchSuccess?.(trimmed);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [onSearchSuccess]);

  useEffect(() => {
    if (prefillCode && prefillCode !== searchedRef.current) {
      searchedRef.current = prefillCode;
      setCode(prefillCode);
      search(prefillCode);
    }
  }, [prefillCode, search]);

  useEffect(() => {
    if (!ticket) return;
    const reqCode = ticket.request.request_code;
    const channel = supabase
      .channel(`support-replies-customer-${reqCode}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_replies' }, async () => {
        const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: reqCode });
        if (!rpcError && data) setTicket(data as TicketData);
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [ticket?.request.request_code]);

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    setAttachment(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setAttachmentPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await search(code);
  };

  const handleReply = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ticket || (!replyText.trim() && !attachment)) return;
    setReplySending(true);
    setUploading(!!attachment);
    setReplyError('');
    try {
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;
      if (attachment) {
        const path = `anonymous/${ticket.request.request_code}/${Date.now()}-${sanitizeFileName(attachment.name)}`;
        const { error: upErr } = await supabase.storage
          .from('support-attachments')
          .upload(path, attachment, { cacheControl: '3600', upsert: false });
        if (upErr) throw upErr;
        const { data: pubData } = supabase.storage.from('support-attachments').getPublicUrl(path);
        attachmentUrl = pubData.publicUrl;
        attachmentName = attachment.name;
      }
      const { error: rpcError } = await supabase.rpc('reply_support_request_customer', {
        p_code: ticket.request.request_code,
        p_content: replyText.trim(),
        p_attachment_url: attachmentUrl,
        p_attachment_name: attachmentName,
      });
      if (rpcError) throw rpcError;
      setReplyText('');
      clearAttachment();
      const { data, error: reloadError } = await supabase.rpc('get_support_request', { p_code: ticket.request.request_code });
      if (reloadError) throw reloadError;
      setTicket(data as TicketData);
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : 'Không thể gửi phản hồi. Vui lòng thử lại.');
    } finally {
      setReplySending(false);
      setUploading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Nhập mã yêu cầu (VD: AB12CD34)"
          className="flex-1 px-4 py-3 rounded-md bg-background-50 border border-background-300/60 text-sm font-mono tracking-widest text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 uppercase"
        />
        <button
          type="submit"
          disabled={loading || !code.trim()}
          className="px-6 py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
        >
          {loading ? 'Đang tra cứu...' : 'Tra cứu'}
        </button>
      </form>

      {error && (
        <div className="mb-5 rounded-lg bg-rose-500/15 border border-rose-500/30 p-4 flex items-center gap-3">
          <i className="ri-error-warning-line text-rose-400 text-xl"></i>
          <p className="text-sm text-rose-400">{error}</p>
        </div>
      )}

      {notFound && (
        <div className="rounded-xl border border-background-200/60 bg-background-50 p-8 text-center">
          <i className="ri-search-eye-line text-4xl text-foreground-500"></i>
          <p className="mt-3 text-foreground-950 font-semibold">Không tìm thấy yêu cầu</p>
          <p className="mt-1 text-sm text-foreground-600">Vui lòng kiểm tra lại mã yêu cầu đã nhập.</p>
        </div>
      )}

      {ticket && (
        <div className="space-y-5">
          <div className="rounded-xl border border-background-200/60 bg-background-50 overflow-hidden">
            <div className="px-5 py-4 border-b border-background-200/60 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs text-foreground-600">Mã yêu cầu</p>
                <p className="font-mono text-lg font-bold tracking-widest text-foreground-950">{ticket.request.request_code}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusMeta[ticket.request.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}>
                {statusMeta[ticket.request.status]?.label || ticket.request.status}
              </span>
            </div>
            <div className="px-5 py-4">
              <h3 className="text-base font-bold text-foreground-950">{ticket.request.subject}</h3>
              <p className="mt-1 text-xs text-foreground-500">{formatDate(ticket.request.created_at)}</p>
              <div className="mt-3 rounded-lg bg-background-100 border border-background-200/60 p-4">
                <p className="text-sm text-foreground-800 whitespace-pre-wrap">{ticket.request.message}</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-base font-bold text-foreground-950 mb-3">Phản hồi ({ticket.replies.length})</h4>
            {ticket.replies.length === 0 ? (
              <div className="rounded-xl border border-background-200/60 bg-background-50 p-6 text-center">
                <p className="text-sm text-foreground-600">Chưa có phản hồi nào. Vui lòng kiên nhẫn chờ đợi.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ticket.replies.map((r, i) => (
                  <div key={i} className={`rounded-xl p-4 border ${r.is_staff ? 'bg-primary-500/10 border-primary-500/20' : 'bg-background-50 border-background-200/60'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${r.is_staff ? 'bg-primary-500/20 text-primary-400' : 'bg-foreground-200/40 text-foreground-700'}`}>
                        {r.is_staff ? 'Hỗ trợ viên' : 'Bạn'}
                      </span>
                      <span className="text-xs text-foreground-500">{formatDate(r.created_at)}</span>
                    </div>
                    {r.content && <p className="text-sm text-foreground-800 whitespace-pre-wrap">{r.content}</p>}
                    {r.attachment_url && (
                      isImageAttachment(r) ? (
                        <a href={r.attachment_url} target="_blank" rel="noreferrer noopener" className="mt-2 block">
                          <img
                            src={r.attachment_url}
                            alt={r.attachment_name || 'Ảnh đính kèm'}
                            className="max-w-full max-h-56 rounded-md border border-background-200/60 object-contain bg-background-100 cursor-pointer"
                          />
                        </a>
                      ) : (
                        <a
                          href={r.attachment_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 border border-background-200/60 text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
                        >
                          <i className="ri-file-download-line text-base"></i>
                          <span className="truncate max-w-[220px]">{r.attachment_name || 'Tệp đính kèm'}</span>
                        </a>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-background-200/60 bg-background-50 p-5">
            <h4 className="text-base font-bold text-foreground-950 mb-2">Nhắn thêm cho hỗ trợ viên</h4>
            <p className="text-sm text-foreground-600 mb-4">Nếu bạn muốn bổ sung thông tin hoặc trả lời lại, hãy nhắn bên dưới. Bạn có thể đính kèm hình ảnh hoặc file.</p>
            {replyError && (
              <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-rose-400 text-base"></i>
                <p className="text-sm text-rose-400">{replyError}</p>
              </div>
            )}
            <form onSubmit={handleReply} className="space-y-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                maxLength={2000}
                rows={3}
                placeholder="Viết phản hồi của bạn..."
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none"
              />

              {attachment && (
                <div className="flex items-center gap-2 rounded-md bg-background-100 border border-background-200/60 p-2">
                  {attachmentPreview ? (
                    <img src={attachmentPreview} alt="Xem trước" className="w-12 h-12 rounded-md object-cover border border-background-200/60" />
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                      <i className="ri-file-line text-xl"></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground-900 truncate">{attachment.name}</p>
                    <p className="text-[10px] text-foreground-500">{(attachment.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:text-rose-400 hover:bg-rose-500/10 cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.txt,.zip,.doc,.docx,.xls,.xlsx"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              />

              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer transition-colors"
                    title="Đính kèm hình ảnh / file"
                  >
                    <i className="ri-attachment-2 text-base"></i>
                  </button>
                  <p className="text-xs text-foreground-500">{replyText.length}/2000</p>
                </div>
                <button
                  type="submit"
                  disabled={replySending || (!replyText.trim() && !attachment)}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {uploading ? 'Đang tải lên...' : replySending ? 'Đang gửi...' : 'Gửi phản hồi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}